import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import PostAddRoundedIcon from '@mui/icons-material/PostAddRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import { alpha, Box, Chip, Grid2 as Grid, Paper, Stack, Typography, useTheme } from '@mui/material';

type ModernBookHeaderProps = {
  companyCount: number;
  productCount: number;
  contactCount: number;
};

type SummaryItemProps = {
  icon: ReactNode;
  label: ReactNode;
  value: number;
};

const SummaryItem = ({ icon, label, value }: SummaryItemProps) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        p: 1.5,
        height: '100%',
        minWidth: 0,
        border: '1px solid',
        borderColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.26 : 0.14),
        borderRadius: 2,
        bgcolor: alpha(theme.palette.background.default, theme.palette.mode === 'dark' ? 0.56 : 0.76),
      }}>
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Box
          aria-hidden="true"
          sx={{
            width: 34,
            height: 34,
            display: 'grid',
            flexShrink: 0,
            placeItems: 'center',
            color: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.dark,
            borderRadius: 1.5,
            bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.1),
            '& .MuiSvgIcon-root': { fontSize: 19 },
          }}>
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
            {label}
          </Typography>
          <Typography variant="subtitle2" sx={{ mt: 0.25, fontWeight: 700 }}>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

const ModernBookHeader = ({ companyCount, productCount, contactCount }: ModernBookHeaderProps) => {
  const theme = useTheme();

  return (
    <Paper
      component="header"
      elevation={0}
      sx={{
        mb: 2.5,
        p: { xs: 2, md: 3 },
        overflow: 'hidden',
        position: 'relative',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2.5,
        background:
          theme.palette.mode === 'dark'
            ? `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.18)} 0%, ${alpha(
                theme.palette.background.paper,
                0.96
              )} 58%)`
            : `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.2)} 0%, ${alpha(
                theme.palette.background.default,
                0.98
              )} 62%)`,
        '&::after': {
          content: '""',
          position: 'absolute',
          width: 220,
          height: 220,
          top: -130,
          right: -70,
          borderRadius: '50%',
          pointerEvents: 'none',
          bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.08 : 0.055),
        },
      }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
        sx={{ position: 'relative', zIndex: 1 }}>
        <Stack direction="row" spacing={1.75} sx={{ minWidth: 0 }}>
          <Box
            aria-hidden="true"
            sx={{
              width: 50,
              height: 50,
              display: 'grid',
              flexShrink: 0,
              placeItems: 'center',
              color: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.dark,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.11),
              '& .MuiSvgIcon-root': { fontSize: 27 },
            }}>
            <PostAddRoundedIcon />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="h1"
              variant="h4"
              sx={{ fontSize: { xs: '1.35rem', md: '1.5rem' }, fontWeight: 700, lineHeight: 1.2 }}>
              <FormattedMessage id="book.modern.title" />
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 720 }}>
              <FormattedMessage id="book.modern.subtitle" />
            </Typography>
          </Box>
        </Stack>
        <Chip
          size="small"
          color="primary"
          variant="outlined"
          label={<FormattedMessage id="book.modern.status.new" />}
          sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, borderRadius: '999px', fontWeight: 700 }}
        />
      </Stack>

      <Grid container spacing={1.5} sx={{ mt: 2.5, position: 'relative', zIndex: 1 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <SummaryItem
            icon={<BusinessRoundedIcon />}
            label={<FormattedMessage id="book.modern.summary.companies" />}
            value={companyCount}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <SummaryItem
            icon={<GroupsRoundedIcon />}
            label={<FormattedMessage id="book.modern.summary.contacts" />}
            value={contactCount}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <SummaryItem
            icon={<Inventory2RoundedIcon />}
            label={<FormattedMessage id="book.modern.summary.products" />}
            value={productCount}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default ModernBookHeader;
